import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { DollarSign, Plus, Edit, Search } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    paymentId: '',
    userId: '',
    amount: '',
    method: 'UPI',
    status: 'Pending'
  });

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPayment) {
        await axios.put(`${API}/admin/payments/${editingPayment._id}`, formData);
        toast({ title: 'Success', description: 'Payment updated successfully' });
      } else {
        await axios.post(`${API}/admin/payments`, formData);
        toast({ title: 'Success', description: 'Payment created successfully' });
      }
      setDialogOpen(false);
      setEditingPayment(null);
      resetForm();
      fetchPayments();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Operation failed', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      paymentId: '',
      userId: '',
      amount: '',
      method: 'UPI',
      status: 'Pending'
    });
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      paymentId: payment.paymentId,
      userId: payment.userId,
      amount: payment.amount || 0,
      method: payment.method || 'UPI',
      status: payment.status
    });
    setDialogOpen(true);
  };

  const filteredPayments = payments.filter(payment => {
    const search = searchTerm.toLowerCase();
    return (
      payment.paymentId?.toLowerCase().includes(search) ||
      payment.clientId?.toString().includes(search) ||
      payment.status?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-blue-600">Payments Management</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setEditingPayment(null); }} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-green-600">{editingPayment ? 'Edit Payment' : 'Create New Payment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-2 block">Payment ID</Label>
                  <Input
                    value={formData.paymentId}
                    onChange={(e) => setFormData({ ...formData, paymentId: e.target.value })}
                    placeholder="PAY-12345"
                    required
                    className="bg-slate-50 border-slate-200 text-slate-800"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 mb-2 block">User</Label>
                  <Select value={formData.userId} onValueChange={(value) => setFormData({ ...formData, userId: value })} required>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.principalId} value={user.principalId}>
                          #{user.clientId} - {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 mb-2 block">Amount (₹)</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="1000"
                    required
                    className="bg-slate-50 border-slate-200 text-slate-800"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 mb-2 block">Method</Label>
                  <Select value={formData.method} onValueChange={(value) => setFormData({ ...formData, method: value })}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-600 mb-2 block">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Successful">Successful</SelectItem>
                    <SelectItem value="Not Issued">Not Issued</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
                {editingPayment ? 'Update Payment' : 'Create Payment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Payment ID, Client ID, Status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      <div className="grid gap-4">
        {filteredPayments.map(payment => (
          <Card key={payment._id} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Payment ID</p>
                    <p className="font-semibold text-blue-300">{payment.paymentId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Client</p>
                    <p className="text-slate-800">#{payment.clientId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Amount</p>
                    <p className="text-green-300 font-bold">₹{payment.amount || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Status</p>
                    <span className="inline-block px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-300">{payment.status}</span>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => handleEdit(payment)}>
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PaymentsManagement;